<i18n>
{
	"en": {
		"theDeviation": "the deviation",
		"cancelLabel": "Cancel",
		"editDeviation": "Edit Deviation {number}",
		"newDeviation": "Create new deviation",
		"save": "Save",
		"saveAndClose": "Save & close",
		"deviationNumber": "Deviation No.",
		"failedToSave": "Failed to save deviation",
		"tripWithoutID": "The referenced trip must be saved before the deviation.",
		"validationErrors": "Validation error. To save the deviation the errors must be corrected as described in the schema.",
		"deviationSaved": "The deviation was saved",
		"allreadyUpToDate": "No changes has been done"
	},
	"no": {
		"theDeviation": "avviket",
		"cancelLabel": "Avbryt",
		"editDeviation": "Rediger avvik {number}",
		"newDeviation": "Opprett nytt avvik",
		"save": "Lagre",
		"saveAndClose": "Lagre & lukk",
		"deviationNumber": "Avvik Nr.",
		"failedToSave": "Kunne ikke lagre avvik",
		"tripWithoutID": "Den refererte turen må lagres før avviket lagres.",
		"validationErrors": "Feil ved validering. For å lagre avviken må feilene rettes slik som angitt i skjemaet.",
		"deviationSaved": "Avviket ble lagret",
		"allreadyUpToDate": "Ingen endringer er gjort"
	}
}
</i18n>

<template>
	<t-dialog
		large
		:isShown="dialogIsShown"
		:confirmLabel="$t('save')"
		:cancelLabel="$t('cancelLabel')"
		disableCloseOnConfirm
		alwaysRememberFocus
		:hasHeader="false"
		bodyStyle="margin-top: 0px"
		:busy="savePending"
		:keyListeners="getKeyListeners()"
		@closing="checkClose"
		@close="onClose"
	>
		<t-vertical-panel>
			<div slot="header" class="header">
				<t-panel-header
					:text="deviation.id ? $t('editDeviation', {number: deviation.deviationNumber}) : $t('newDeviation')"
					:icon="deviation.id ? 'draw' : 'add'"
					type="bp"
					textSize="large"
				/>
			</div>

			<NewDeviationMain class="new-deviation-inner" :deviation="deviation"/>
		</t-vertical-panel>
		<template #footer>
			<div style="width: 100%; display: flex; justify-content: space-between">
				<div style="display:flex"/>

				<div style="display:flex">
					<t-button
						:value="$t('cancelLabel')"
						appearance="default"
						ointent="default"
						@click="onCancel"
					/>
					<t-button
						:value="$t('saveAndClose')"
						:appearance="'primary'"
						style="margin-left: 15px;"
						@click="saveDeviationAndClose()"
					/>
					<t-button
						:value="$t('save')"
						:appearance="'primary'"
						style="margin-left: 15px;"
						@click="saveDeviation()"
					/>
				</div>
			</div>
		</template>

		<ConfirmUnsavedChanges
			:isShown="confirmUnsavedChanges"
			:subject="$t('theDeviation')"
			@cancel="toggleUnsavedChanges(false)"
			@save="saveDeviationAndClose"
			@close="onCloseDirty"
		/>
	</t-dialog>
</template>

<script>
import ConfirmUnsavedChanges from '@/components/modals/ConfirmUnsavedChanges';
import NewDeviationMain from './NewDeviationMain';

import { eventBus, } from '@/core';
import { mapState, mapActions } from './store/newDeviation.store.js';

export default {
	name: 'NewDeviation',
	components: {
		ConfirmUnsavedChanges,
		NewDeviationMain
	},

	data() {
		return {
			confirmUnsavedChanges: false,
			closeDirty: false,
			savePending: false,
		};
	},

	computed: {
		...mapState([
			'deviation',
			'dirty',
			'dialogIsShown',
		]),
		isNewDeviation() {
			return !this.deviation || !this.deviation.id;
		},
	},
	async created() {
		eventBus.$on('openNewDeviation', this.newDeviation);
		eventBus.$on('loadDeviation', this.loadDeviation);
	},
	beforeDestroy() {
		eventBus.$off('openNewDeviation');
		eventBus.$off('loadDeviation');
	},

	methods: {
		...mapActions([
			'closeDialog',
			'saveNewDeviation',
			'updateDeviation',
			'loadDeviation',
			'newDeviation',
			'returnDeviation'
		]),
		toggleUnsavedChanges(value) {
			this.confirmUnsavedChanges = value;
		},

		async saveDeviation({ closeDialog=false }={}) {
			this.toggleUnsavedChanges(false);
			this.savePending = true;
			let savedSuccessfully = false;
			try {
				this.$notify({ clean: true });
				let allreadyUpToDate = false;
				if (this.deviation.id) {
					allreadyUpToDate = await this.updateDeviation({ closeDialog });
				} else {
					await this.saveNewDeviation({ closeDialog });
				}
				if (allreadyUpToDate) {
					this.$notify({
						type: 'success',
						title: this.$t('allreadyUpToDate'),
					});
				} else {
					this.$notify({
						type: 'success',
						title: this.$t('deviationSaved')
					});
				}
				savedSuccessfully = true;
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$te(e.message) ? this.$t(e.message) : this.$t('failedToSave'),
				});
			}
			this.savePending = false;
			return savedSuccessfully;
		},
		async saveDeviationAndClose() {
			let savedSuccessfully = await this.saveDeviation({ closeDialog: true });
			if (savedSuccessfully) this.onClose();
		},

		onCancel() {
			if (this.dirty && !this.closeDirty) {
				this.toggleUnsavedChanges(true);
			} else {
				this.onClose();
			}
		},
		checkClose(abort) {
			if (this.dirty && !this.closeDirty) {
				abort(true);
				this.toggleUnsavedChanges(true);
			} else {
				abort(false);
			}
			this.closeDirty = false;
		},
		onCloseDirty() {
			this.closeDirty = true;
			this.onClose();
		},
		onClose() {
			this.closeDialog();
			this.toggleUnsavedChanges(false);
			this.$notify({ clean: true });
		},

		getKeyListeners() {
			return [
				{ 	key: 'S',
					listener: this.saveDeviation,
					modifiers: { ctrlKey: true, shiftKey: undefined, altKey: false, stop: true, prevent: true } },
				{ 	key: 'Enter',
					listener: this.saveDeviationAndClose,
					modifiers: { ctrlKey: true, shiftKey: false, altKey: false, stop: true, prevent: true } },
			];
		},
	},

};
</script>

<style lang="scss" scoped>
	.header {
		display: grid;
		column-gap: 5px;
		grid-auto-flow: column;
		grid-auto-columns: minmax(min-content, max-content);
		align-content: end;
	}
	.new-deviation-inner {
		width: 1000px;
		padding: 5px 10px 5px 5px;
	}
</style>
